import React, {useCallback, useEffect, useState} from 'react'
import api from "../../Services/api"
import {Button, Stack, Typography} from "@mui/material"
import Grid from "@mui/material/Grid2";
import { ReservationFound } from './ReservationFound'
import { NewReservation } from './NewReservation'
import { SelectDriveIn } from './SelectDriveIn'
import { RegisterNumberFieldSimple } from '../RegisterNumberField'
import { Form } from 'react-final-form'
import BackButton from './Common/BackButton'
import { ServiceCardType } from './Types'
import {useTranslation} from "react-i18next"
import {useDispatch, useSelector} from "react-redux"
import {RootState} from "../../Redux"
import SelfServiceRedux from '../../Redux/selfServiceRedux'
import {SelfServiceSettingsType} from '../../interfaces'

const { getReservationWithRegistrationNumber } = api.create()

export const RegistrationNumberInput = () => {
    const dispatch = useDispatch()
    const { t } = useTranslation()

    const [driveIn, setDriveIn] = useState<boolean | null>(null)
    const [registrationNumber, setRegistrationNumber] = useState('')
    const [serviceCards, setServiceCards] = useState<ServiceCardType[]>([])
    const [serviceCard, setServiceCard] = useState<ServiceCardType>()
    const [serviceCardFound, setServiceCardFound] = useState(null)
    const [hasOtherServiceCard, setHasOtherServiceCard] = useState<boolean>(false) // hasOtherServiceCard,
    const [fetching, setFetching] = useState<boolean>(false)
    const [searchMessage, setSearchMessage] = useState<boolean>(false)

    const chainId = useSelector((state: RootState) => state.ui?.settings?.chain?.id)
    const stationId = useSelector((state: RootState) => state.ui?.settings?.station?.id)
    const customerToken = useSelector((state: RootState) => state.ui.customer.token)
    const settings: SelfServiceSettingsType = useSelector((state: RootState) => state.ui.settings?.selfService ?? {});

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement|HTMLTextAreaElement>) => {
        setRegistrationNumber(event.target.value)
    }

    const fetchReservation = useCallback(async () => {
        if (chainId && stationId && driveIn !== null && (registrationNumber || customerToken)) {
            setFetching(true)
            const response = await getReservationWithRegistrationNumber(
                chainId,
                stationId,
                registrationNumber,
                customerToken
            )
            setFetching(false)
            if (response.ok) {
                const data = (response.data ?? []) as ServiceCardType[]

                return data?.length > 0 ? data : null
            }
        }

        return null
    }, [chainId, customerToken, driveIn, registrationNumber, stationId])

    useEffect(() => {
        if (driveIn !== false) {
            setSearchMessage(null)
            fetchReservation().then((data) => {
                setServiceCard(data?.[0] as ServiceCardType)
                setServiceCards((data ?? []) as ServiceCardType[])
                setSearchMessage(data?.length > 0 ? null : t('selfService.reservationNotFound'))
            })
        } else {
            setServiceCard(undefined)
            setServiceCards([])
        }
    }, [driveIn, fetchReservation, serviceCardFound, t])

    useEffect(() => {
        if (driveIn === null && settings.bookingAvailable === false) {
            setDriveIn(true)
        }
    }, [driveIn, settings]);

    useEffect(() => {
        dispatch(SelfServiceRedux.setServiceCardInfo(
            serviceCard?.token,
            ! serviceCard?.token && ! serviceCardFound,
            !! serviceCard?.confirmIn
        ))
    }, [dispatch, serviceCard?.token, serviceCard?.confirmIn, serviceCardFound]);

    const clickPlate = (card) => {
        setServiceCard(card)
        setRegistrationNumber(
            card.sale?.tasks?.[0]?.vehicle?.registrationNumber,
        )
        setTimeout(() => handleContinue(), 0)
    }

    const handleContinue = async () => {
        // Logic for continue action
        if (registrationNumber === '' && !customerToken) {
            return
        }

        if (driveIn === false) {
            fetchReservation().then((data) => {
                setHasOtherServiceCard(data?.filter((card) => card.sale?.tasks?.[0]?.vehicle?.registrationNumber === registrationNumber)?.length > 0)
                setServiceCardFound(false)
            })
        } else {
            setServiceCardFound(!! serviceCard)
        }
    }

    const backFromSelectRegistrationNumber = () => {
        if (settings.bookingAvailable === false) {
            window.location.reload();
        } else {
            setDriveIn(null)
            setRegistrationNumber('')
            setServiceCardFound(null)
            setServiceCard(undefined)
            setServiceCards([])
        }
    }

    const backFromServiceSelection = () => {
        setServiceCardFound(null)
        setServiceCard(undefined)
        setServiceCards([])
    }

    function selectRegistrationNumber() {
        return (
            <Grid container wrap="nowrap" direction="column" justifyContent="space-between" alignItems="center" spacing={4} className={'pageMainContainer'}>
                <Grid>
                </Grid>
                <Grid>
                    <Stack direction="column" spacing={4} alignItems="center">
                        <div style={{textAlign: 'center'}}>
                            <Typography variant="h4" gutterBottom>
                                {t('selfService.enterRegistrationNumber')}
                            </Typography>
                            <Form
                                onSubmit={() => ! fetching && (driveIn === false || settings.driveInAvailable || serviceCards?.length > 0) && handleContinue()}
                                id="registerForm"
                                initialValues={{registerNumber: registrationNumber ?? ''}}
                                render={({ handleSubmit }) => (
                                    <form onSubmit={handleSubmit}>
                                        <RegisterNumberFieldSimple
                                            id="RegistrationNumberInput"
                                            disabled={false}
                                            textFieldProps={{
                                                className: 'registrationNumberInput',
                                                variant: 'outlined',
                                                helperText: t('selfService.enterRegistrationNumberInfo'),
                                                style: {textAlign: 'start', fontFamily: 'Koulen', fontWeight: 500, lineHeight: 1.0},
                                                inputProps: {
                                                    style: {paddingLeft: '1.5rem'}
                                                },
                                            }}
                                            onChange={handleInputChange}
                                            showClearButton
                                            scale={1.25}
                                        />
                                    </form>
                                )}
                            />
                        </div>
                        <Stack direction="row" spacing={2} height={100}>
                            {serviceCards.length > 0 && driveIn !== false && serviceCards.map((card) => (
                                <Button
                                    key={card.token}
                                    disabled={fetching}
                                    color="secondary"
                                    variant="contained"
                                    size="small"
                                    className="buttonNarrow"
                                    onClick={() => clickPlate(card)}
                                >
                                    {
                                        card.sale.tasks?.[0]?.vehicle
                                            ?.registrationNumber
                                    }
                                </Button>
                            ))}
                            {searchMessage && driveIn && ! settings.driveInAvailable && registrationNumber?.length > 2 &&
                                <Typography>
                                    {searchMessage}
                                </Typography>
                            }
                        </Stack>
                    </Stack>
                </Grid>
                <Grid className="footerButtons">
                    <Grid container wrap="nowrap" spacing={4} justifyContent="space-between" alignItems="end">
                        <Grid>
                            <BackButton onBack={backFromSelectRegistrationNumber} />
                        </Grid>
                        <Grid>
                            <Button
                                disabled={fetching || (driveIn && ! settings.driveInAvailable && serviceCards?.length < 1)}
                                variant="contained"
                                color="secondary"
                                onClick={handleContinue}
                            >
                                {t('common.continue')}
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        )
    }

    return (
        <>
            {driveIn === null && <SelectDriveIn onSelect={setDriveIn} />}
            {driveIn !== null && (
                <>
                    {serviceCardFound === null && selectRegistrationNumber()}
                    {serviceCardFound === true && serviceCard && (
                        <ReservationFound
                            serviceCard={serviceCard}
                            setServiceCard={setServiceCard}
                            onBack={backFromServiceSelection}
                        />
                    )}
                    {serviceCardFound === false && (
                        <NewReservation
                            registrationNumber={registrationNumber}
                            isDriveIn={driveIn}
                            hasOtherReservation={hasOtherServiceCard}
                            setServiceCard={setServiceCard}
                            onBack={backFromServiceSelection}
                            currentServiceCard={serviceCard}
                        />
                    )}
                </>
            )}
        </>
    )
}
